import { Link } from 'react-router-dom';
import { useJobs } from '../../../hooks/unified/useJobsQueries';
import { env } from '../../../config/env';

interface SimilarJobsSectionProps {
  jobId: string;
}

export function SimilarJobsSection({ jobId }: SimilarJobsSectionProps) {
  // Fetch real jobs from API
  const { data: jobsData, isLoading } = useJobs();
  
  // Filter out the current job and limit to 3 similar jobs
  const similarJobs = (jobsData || []).filter((job: any) => job.id !== jobId).slice(0, 3);

  // Helper function to get full image URL
  const getImageUrl = (imageUrl: string | null | undefined): string | null => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    return `${env.API_BASE_URL}${imageUrl}`;
  };

  const formatSalary = (salaryMin?: number, salaryMax?: number) => {
    if (salaryMin && salaryMax) {
      return `INR ${(salaryMin / 1000).toFixed(0)}k - ${(salaryMax / 1000).toFixed(0)}k`;
    }
    return 'Competitive';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Similar Jobs</h2>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (similarJobs.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Similar Jobs</h2>
      <div className="space-y-4">
        {similarJobs.map((job: any) => (
          <Link
            key={job.id}
            to={`/jobs/${job.id}`}
            className="block p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-start gap-4">
              {job.company_logo_url && getImageUrl(job.company_logo_url) && (
                <img
                  src={getImageUrl(job.company_logo_url)!}
                  alt={`${job.company} logo`}
                  className="w-12 h-12 rounded-lg object-cover bg-gray-100 border border-gray-200 flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 mb-1 hover:text-primary-600 transition-colors">
                  {job.title}
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {job.company}
                  </span>
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {job.location}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-primary-600">
                    {formatSalary(job.salary_min, job.salary_max)}
                  </span>
                  {job.job_type && (
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                      {job.job_type}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
